import React, { useState } from 'react'
import { Link } from 'react-router-dom'
import toast from 'react-hot-toast'
import { uploadDocuments, deleteDocument } from 'services/documents.service'
import StatusBadge from 'components/StatusBadge'
import { useI18n, apiError } from 'i18n'

const DocumentsTab = ({ shipment, readOnly, onChange }) => {
  const { t, fmt } = useI18n()
  const [files, setFiles] = useState([])
  const [busy, setBusy] = useState(false)

  const onUpload = async (event) => {
    event.preventDefault()
    if (!files.length) {
      toast.error(t('documents.pickFiles'))
      return
    }
    setBusy(true)
    try {
      const { documents } = await uploadDocuments(shipment.shipmentId, files)
      const failed = documents.filter((d) => d.status === 'failed').length
      if (failed) toast.error(t('documents.failedCount', { count: failed }))
      else toast.success(t('documents.processed', { count: documents.length }))
      setFiles([])
      event.target.reset()
      onChange()
    } catch (error) {
      toast.error(apiError(error, t('documents.uploadFailed')))
    } finally {
      setBusy(false)
    }
  }

  const onDelete = async (doc) => {
    if (!window.confirm(t('documents.confirmDelete', { file: doc.fileName }))) return
    try {
      await deleteDocument(doc.documentId)
      toast.success(t('documents.deleted'))
      onChange()
    } catch (error) {
      toast.error(apiError(error, t('documents.deleteFailed')))
    }
  }

  return (
    <div className='space-y-4'>
      {!readOnly && (
        <form onSubmit={onUpload} className='space-y-2 rounded-xl border border-dashed border-slate-300 bg-white p-4'>
          <div className='flex flex-wrap items-center gap-3'>
            <input type='file' multiple accept='application/pdf,image/*,.xml,text/xml'
              onChange={(e) => setFiles(Array.from(e.target.files || []))} className='text-sm' />
            <button type='submit' disabled={busy}
              className='rounded-lg bg-accent px-4 py-2 text-sm font-semibold text-ink hover:bg-accent-600 disabled:opacity-50'>
              {busy ? t('documents.uploading') : t('documents.upload')}
            </button>
          </div>
          <p className='text-xs text-slate-500'>{t('documents.uploadHelp')}</p>
        </form>
      )}

      <div className='overflow-x-auto rounded-xl border border-slate-200 bg-white'>
        <table className='min-w-full divide-y divide-slate-200 text-sm'>
          <thead className='bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500'>
            <tr>
              <th className='px-4 py-3'>{t('common.status')}</th>
              <th className='px-4 py-3'>{t('documents.file')}</th>
              <th className='px-4 py-3'>{t('documents.type')}</th>
              <th className='px-4 py-3'>{t('documents.method')}</th>
              <th className='px-4 py-3 text-right'>{t('documents.confidence')}</th>
              <th className='px-4 py-3'>{t('documents.uploaded')}</th>
              <th className='px-4 py-3 text-right'>{t('common.actions')}</th>
            </tr>
          </thead>
          <tbody className='divide-y divide-slate-100'>
            {shipment.documents.length === 0 && (
              <tr><td colSpan={7} className='px-4 py-8 text-center text-slate-400'>{t('documents.empty')}</td></tr>
            )}
            {shipment.documents.map((d) => (
              <tr key={d.documentId} className='hover:bg-slate-50'>
                <td className='px-4 py-3'><StatusBadge status={d.status} /></td>
                <td className='px-4 py-3 font-medium'>
                  {d.fileName}
                  {d.error && <p className='text-xs font-normal text-status-incomplete'>{d.error}</p>}
                </td>
                <td className='px-4 py-3 text-slate-600'>
                  {d.docType ? t(`docTypes.${d.docType}`) : <span className='text-status-review'>{t('documents.unclassified')}</span>}
                  {d.classificationConfidence !== null && d.classificationConfidence < 1 && (
                    <span className='ml-1 text-xs text-slate-400'>({fmt.pct(d.classificationConfidence)})</span>
                  )}
                </td>
                <td className='px-4 py-3 text-slate-600'>{d.extractionMethod ? t(`methods.${d.extractionMethod}`) : '—'}</td>
                <td className='px-4 py-3 text-right tabular-nums text-slate-600'>{fmt.pct(d.extractionConfidence)}</td>
                <td className='px-4 py-3 text-slate-600'>{fmt.date(d.createdAt)}</td>
                <td className='px-4 py-3'>
                  <div className='flex items-center justify-end gap-3'>
                    <Link to={`/documents/${d.documentId}`} className='font-semibold text-brand hover:underline'>{t('common.open')}</Link>
                    {!readOnly && <button onClick={() => onDelete(d)} className='font-semibold text-status-incomplete hover:underline'>{t('common.delete')}</button>}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

export default DocumentsTab
